/**
 * Enhanced Offline Sync Service
 * Handles robust offline data synchronization with Capacitor
 */

import { getQueuedRequests, retryQueuedRequests, clearStaleRequests, getQueueCount } from '../utils/requestQueue';
import { getNetworkStatus, addNetworkStatusListener } from '../utils/capacitorNetwork';
import { getStorageJSON, setStorageJSON } from '../utils/capacitorStorage';
import api from './api';

const SYNC_INTERVAL = 30000; // 30 seconds
const MAX_SYNC_RETRIES = 3;
const STALE_REQUEST_AGE_HOURS = 24;

class OfflineSyncService {
  constructor() {
    this.isSyncing = false;
    this.syncInterval = null;
    this.networkListener = null;
    this.syncListeners = new Set();
  }

  /**
   * Initialize the sync service
   */
  async initialize() {
    // Clear stale requests on startup
    await clearStaleRequests(STALE_REQUEST_AGE_HOURS);

    // Set up network status listener
    this.networkListener = addNetworkStatusListener(async (status) => {
      if (status.connected && !this.isSyncing) {
        console.log('Network connected, starting sync...');
        await this.sync();
      }
    });

    // Start periodic sync
    this.startPeriodicSync();

    // Sync immediately if online
    const networkStatus = await getNetworkStatus();
    if (networkStatus.connected) {
      await this.sync();
    }
  }

  /**
   * Start periodic sync
   */
  startPeriodicSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }

    this.syncInterval = setInterval(async () => {
      const networkStatus = await getNetworkStatus();
      if (networkStatus.connected && !this.isSyncing) {
        await this.sync();
      }
    }, SYNC_INTERVAL);
  }

  /**
   * Stop periodic sync
   */
  stopPeriodicSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  /**
   * Sync queued requests
   */
  async sync() {
    if (this.isSyncing) {
      console.log('Sync already in progress');
      return;
    }

    const networkStatus = await getNetworkStatus();
    if (!networkStatus.connected) {
      console.log('Not connected, skipping sync');
      return;
    }

    this.isSyncing = true;
    this.notifyListeners({ type: 'sync_start' });

    try {
      // Clear stale requests first
      const cleared = await clearStaleRequests(STALE_REQUEST_AGE_HOURS);
      if (cleared > 0) {
        console.log(`Cleared ${cleared} stale requests`);
      }

      // Get queued requests
      const queued = await getQueuedRequests();
      if (queued.length === 0) {
        this.isSyncing = false;
        this.notifyListeners({ type: 'sync_complete', success: 0, failed: 0 });
        return;
      }

      console.log(`Syncing ${queued.length} queued requests...`);

      // Retry queued requests
      const results = await retryQueuedRequests(async (requestData) => {
        return api({
          url: requestData.url,
          method: requestData.method,
          data: requestData.data,
          headers: requestData.headers,
        });
      });

      console.log(`Sync complete: ${results.success} succeeded, ${results.failed} failed`);

      this.notifyListeners({
        type: 'sync_complete',
        success: results.success,
        failed: results.failed,
        errors: results.errors,
      });

      // Dispatch custom event for UI updates
      window.dispatchEvent(new CustomEvent('offlineSyncComplete', { detail: results }));
    } catch (error) {
      console.error('Error during sync:', error);
      this.notifyListeners({
        type: 'sync_error',
        error: error.message,
      });
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Force sync (manual trigger)
   */
  async forceSync() {
    this.stopPeriodicSync();
    await this.sync();
    this.startPeriodicSync();
  }

  /**
   * Get sync status
   */
  async getSyncStatus() {
    const queueCount = await getQueueCount();
    const networkStatus = await getNetworkStatus();

    return {
      isSyncing: this.isSyncing,
      queueCount,
      isOnline: networkStatus.connected,
      connectionType: networkStatus.connectionType,
    };
  }

  /**
   * Add sync status listener
   */
  addSyncListener(callback) {
    this.syncListeners.add(callback);
    return () => {
      this.syncListeners.delete(callback);
    };
  }

  /**
   * Notify all listeners
   */
  notifyListeners(event) {
    this.syncListeners.forEach((callback) => {
      try {
        callback(event);
      } catch (error) {
        console.error('Error in sync listener:', error);
      }
    });
  }

  /**
   * Cleanup
   */
  destroy() {
    this.stopPeriodicSync();
    if (this.networkListener) {
      this.networkListener();
      this.networkListener = null;
    }
    this.syncListeners.clear();
  }
}

// Create singleton instance
const offlineSyncService = new OfflineSyncService();

// Initialize on module load if in browser
if (typeof window !== 'undefined') {
  // Initialize after a short delay to ensure app is ready
  setTimeout(() => {
    offlineSyncService.initialize().catch((error) => {
      console.error('Failed to initialize offline sync service:', error);
    });
  }, 1000);
}

export default offlineSyncService;

